import { apiGet, apiSend, postRefresh } from "../api/client";
import type {
  AcceptInviteOutcome,
  AcceptInviteRequest,
  AuthSession,
  LoginOutcome,
  MfaRequest,
  ProviderList,
} from "../api/auth";
import type { LoginResponse, Me } from "../api/types";
import { setToken } from "./tokenStore";

export function toMe(body: LoginResponse): Me {
  return {
    user: body.user,
    role: body.role,
    entityIds: body.entityIds,
    surface: body.surface,
    portalEntityId: body.portalEntityId ?? null,
  };
}

function adopt(session: AuthSession): AuthSession {
  setToken(session.accessToken);
  return session;
}

/** Leg one: credentials. A session comes back only on the direct path (m6-rulings.md #105). */
export async function login(email: string, password: string): Promise<LoginOutcome> {
  const body = await apiSend<LoginOutcome>("POST", "/api/auth/login", { email, password });
  return body.status === "ok" ? adopt(body) : body;
}

/** The SSO leg: the callback's single-use handoff answers the same union as `/login` (ruling 161). */
export async function completeOidc(handoff: string): Promise<LoginOutcome> {
  const body = await apiSend<LoginOutcome>("POST", "/api/auth/oidc/complete", { handoff });
  return body.status === "ok" ? adopt(body) : body;
}

/** Unauthenticated and secret-free: only tells the sign-in screen whether to show an SSO button. */
export async function providers(): Promise<ProviderList> {
  return apiGet<ProviderList>("/api/auth/providers");
}

/** Leg two: the code and/or the chosen company complete any challenge. */
export async function completeChallenge(input: MfaRequest): Promise<AuthSession> {
  return adopt(await apiSend<AuthSession>("POST", "/api/auth/mfa", input));
}

export async function acceptInvite(input: AcceptInviteRequest): Promise<AcceptInviteOutcome> {
  const body = await apiSend<AcceptInviteOutcome>("POST", "/api/auth/accept-invite", input);
  return body.status === "ok" ? adopt(body) : body;
}

export async function refresh(): Promise<Me> {
  return toMe(await postRefresh());
}

export async function logout(): Promise<void> {
  await apiSend<void>("POST", "/api/auth/logout");
}

export async function me(): Promise<Me> {
  return apiGet<Me>("/api/auth/me");
}
